'use client'

import * as React from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'

export interface StatCardProps {
    label: string
    value: string | number
    trend?: {
        value: string
        tone?: 'positive' | 'neutral'
    }
    href?: string
    /** One quiet line under the numeral, e.g. "Active in your congregation". */
    caption?: string
    /** A sized icon element shown in a round tile at the right. Decorative. */
    icon?: React.ReactNode
    /**
     * `brand` = forest-green block with white text. The white card is the
     * default everywhere; `brand` is reserved for ONE highlighted headline
     * metric per screen (dashboard Households, Tova 2026-09-18).
     */
    tone?: 'default' | 'brand'
    className?: string
    style?: React.CSSProperties
}

export function StatCard({
    label,
    value,
    trend,
    href,
    caption,
    icon,
    tone = 'default',
    className,
    style,
}: StatCardProps) {
    const brand = tone === 'brand'
    const content = (
        <>
            {icon && (
                <span
                    aria-hidden="true"
                    className={cn(
                        'absolute right-5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full',
                        brand
                            ? 'bg-[var(--primary-700)] text-[var(--text-on-brand)]'
                            : 'bg-[var(--bg-brand-subtle)] text-[var(--icon-brand)]'
                    )}
                >
                    {icon}
                </span>
            )}
            <p className={cn(
                'text-xs font-semibold uppercase tracking-[0.08em]',
                brand ? 'text-[var(--primary-100)]' : 'text-[var(--text-tertiary)]'
            )}>
                {label}
            </p>
            <div className="mt-3 flex items-baseline gap-2">
                {/* serif display numerals (ruling 2026-07-31) — tables stay sans */}
                <span className={cn(
                    'font-serif text-4xl font-medium tabular-nums',
                    brand ? 'text-[var(--text-on-brand)]' : 'text-[var(--text-primary)]'
                )}>
                    {value}
                </span>
                {trend && (
                    <span
                        className={cn(
                            'text-sm font-medium',
                            trend.tone === 'positive'
                                ? 'text-[var(--text-success)]'
                                : 'text-[var(--text-tertiary)]'
                        )}
                    >
                        {trend.value}
                    </span>
                )}
            </div>
            {caption && (
                <p className={cn(
                    'mt-1 text-xs',
                    icon && 'pr-12',
                    brand ? 'text-[var(--primary-100)]' : 'text-[var(--text-tertiary)]'
                )}>
                    {caption}
                </p>
            )}
        </>
    )

    const cardClasses = cn(
        'relative block rounded-lg border p-5 transition-all duration-300',
        brand
            ? 'border-[var(--bg-brand)] bg-[var(--bg-brand)]'
            : 'border-[var(--border-subtle)] bg-[var(--bg-surface)]',
        'animate-scale-in opacity-0',
        href && (brand ? 'hover:bg-[var(--bg-brand-hover)]' : 'hover:border-[var(--border-default)]'),
        href && 'hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--border-focus)]',
        className
    )

    if (href) {
        return (
            <Link
                href={href}
                className={cardClasses}
                style={{ ...style, animationFillMode: 'forwards' }}
            >
                {content}
            </Link>
        )
    }

    return (
        <div
            className={cardClasses}
            style={{ ...style, animationFillMode: 'forwards' }}
        >
            {content}
        </div>
    )
}

/**
 * StatCardGrid — seam-grid metric strip (reskin 2026-07-31).
 * Adjacent cells share one 1px hairline seam (gap-px over a border-colored
 * background) instead of stacking two borders. Pass grid-cols-* via
 * className; children are StatCards (their own border/radius are stripped).
 */
export function StatCardGrid({
    className,
    children,
}: {
    className?: string
    children: React.ReactNode
}) {
    return (
        <div
            className={cn(
                'grid gap-px overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-[var(--border-subtle)]',
                '[&>*]:rounded-none [&>*]:border-0 [&>*]:bg-[var(--bg-surface)]',
                className
            )}
        >
            {children}
        </div>
    )
}
